import { createTheme, alpha, type Theme } from '@mui/material/styles';

export type ConstructorAppearance = 'dark' | 'light';

export const APPEARANCE_STORAGE_KEY = 'mv-constructor-appearance';

/** Chrome colours used by canvas layers that still read JS values. */
export interface ConstructorChromeColors {
  canvas: string;
  panel: string;
  panelHover: string;
  divider: string;
  selection: string;
  selectionMuted: string;
  ruler: string;
  dotGrid: string;
  artboardShadow: string;
  statusBar: string;
  toolbar: string;
}

const darkChrome: ConstructorChromeColors = {
  canvas: '#18181b',
  panel: '#27272a',
  panelHover: '#383838',
  divider: '#383838',
  selection: '#8b5cf6',
  selectionMuted: alpha('#8b5cf6', 0.35),
  ruler: '#252525',
  dotGrid: alpha('#FFFFFF', 0.06),
  artboardShadow: '0 18px 50px rgba(0,0,0,0.45)',
  statusBar: '#252525',
  toolbar: '#2C2C2C',
};

const lightChrome: ConstructorChromeColors = {
  canvas: '#e4e4e7',
  panel: '#ffffff',
  panelHover: '#f4f4f5',
  divider: '#e4e4e7',
  selection: '#7c3aed',
  selectionMuted: alpha('#7c3aed', 0.28),
  ruler: '#f4f4f5',
  dotGrid: alpha('#18181b', 0.1),
  artboardShadow: '0 18px 48px rgba(24,24,27,0.12)',
  statusBar: '#fafafa',
  toolbar: '#ffffff',
};

/** @deprecated Prefer constructorChromeFor(appearance). Kept for existing imports. */
export const constructorColors = darkChrome;

export function constructorChromeFor(appearance: ConstructorAppearance): ConstructorChromeColors {
  return appearance === 'light' ? lightChrome : darkChrome;
}

export function detectConstructorAppearance(): ConstructorAppearance {
  if (typeof localStorage === 'undefined') return 'dark';
  const stored = localStorage.getItem(APPEARANCE_STORAGE_KEY);
  if (stored === 'light' || stored === 'dark') return stored;
  if (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: light)').matches) {
    return 'light';
  }
  return 'dark';
}

function buildTheme(appearance: ConstructorAppearance, chrome: ConstructorChromeColors): Theme {
  const isLight = appearance === 'light';
  return createTheme({
    palette: {
      mode: appearance,
      primary: { main: chrome.selection },
      background: {
        default: chrome.canvas,
        paper: chrome.panel,
      },
      text: {
        primary: isLight ? '#18181b' : '#E8E8E8',
        secondary: isLight ? alpha('#18181b', 0.58) : alpha('#E8E8E8', 0.62),
      },
      divider: chrome.divider,
      action: {
        hover: isLight ? alpha('#18181b', 0.05) : alpha('#FFFFFF', 0.06),
        selected: alpha(chrome.selection, isLight ? 0.14 : 0.18),
      },
    },
    shape: { borderRadius: 6 },
    typography: {
      fontFamily: '"Inter Variable", Inter, system-ui, sans-serif',
      overline: { fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.06em' },
      caption: { fontSize: '0.75rem' },
      body2: { fontSize: '0.8125rem' },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          '*::-webkit-scrollbar': { width: 8, height: 8 },
          '*::-webkit-scrollbar-thumb': {
            backgroundColor: isLight ? alpha('#18181b', 0.18) : alpha('#FFFFFF', 0.15),
            borderRadius: 4,
          },
          '*::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
        },
      },
      MuiButton: { defaultProps: { disableElevation: true } },
      MuiIconButton: {
        styleOverrides: {
          root: { borderRadius: 6 },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            border: 'none',
            borderRadius: 6,
            '&.Mui-selected': {
              bgcolor: alpha(chrome.selection, 0.2),
              color: chrome.selection,
            },
          },
        },
      },
      MuiTextField: {
        defaultProps: { size: 'small' },
        styleOverrides: {
          root: {
            '& .MuiInputBase-root': { height: 28, fontSize: '0.8125rem' },
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: { minHeight: 36, fontSize: '0.75rem', textTransform: 'none' },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: { minHeight: 36 },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            bgcolor: 'transparent',
            '&::before': { display: 'none' },
          },
        },
      },
    },
  });
}

export const constructorThemeDark = buildTheme('dark', darkChrome);
export const constructorThemeLight = buildTheme('light', lightChrome);

/** Default export kept for call sites that still import a single theme. */
export const constructorTheme = constructorThemeDark;

export function constructorThemeFor(appearance: ConstructorAppearance): Theme {
  return appearance === 'light' ? constructorThemeLight : constructorThemeDark;
}
